import { motion } from "framer-motion";
import {
  Salad, Bot, Gauge, LineChart, Droplets, Flower2, Watch, ShoppingCart, FileDown, ShieldCheck, Check, HeartPulse,
} from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const meta = [
  { icon: Salad, accent: "emerald" },
  { icon: Bot, accent: "violet" },
  { icon: Gauge, accent: "cyan" },
  { icon: LineChart, accent: "sky" },
  { icon: Droplets, accent: "rose" },
  { icon: Flower2, accent: "pink" },
  { icon: Watch, accent: "teal" },
  { icon: ShoppingCart, accent: "amber" },
  { icon: FileDown, accent: "indigo" },
  { icon: ShieldCheck, accent: "emerald" },
];

const accentMap = {
  emerald: "text-emerald-400 bg-emerald-500/10 group-hover:border-emerald-400/40",
  violet: "text-violet-400 bg-violet-500/10 group-hover:border-violet-400/40",
  cyan: "text-cyan-400 bg-cyan-500/10 group-hover:border-cyan-400/40",
  sky: "text-sky-400 bg-sky-500/10 group-hover:border-sky-400/40",
  rose: "text-rose-400 bg-rose-500/10 group-hover:border-rose-400/40",
  pink: "text-pink-400 bg-pink-500/10 group-hover:border-pink-400/40",
  teal: "text-teal-400 bg-teal-500/10 group-hover:border-teal-400/40",
  amber: "text-amber-400 bg-amber-500/10 group-hover:border-amber-400/40",
  indigo: "text-indigo-400 bg-indigo-500/10 group-hover:border-indigo-400/40",
};
const dotMap = {
  emerald: "text-emerald-400", violet: "text-violet-400", cyan: "text-cyan-400",
  sky: "text-sky-400", rose: "text-rose-400", pink: "text-pink-400",
  teal: "text-teal-400", amber: "text-amber-400", indigo: "text-indigo-400",
};

const container = { hidden: {}, show: { transition: { staggerChildren: 0.06 } } };
const item = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

export const Features = () => {
  const { t } = useLang();
  return (
    <section
      id="fonctionnalites"
      data-testid="features-section"
      className="relative py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400">
            {t.features.overline}
          </span>
          <h2 className="mt-4 font-outfit text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
            {t.features.title}
          </h2>
          <p className="mt-4 text-lg text-slate-400">{t.features.subtitle}</p>
        </div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.1 }}
          className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {t.features.items.map((f, idx) => {
            const m = meta[idx] || meta[0];
            const [iconColor, iconBg, hoverBorder] = accentMap[m.accent].split(" ");
            const Icon = m.icon;
            return (
              <motion.div
                key={idx}
                variants={item}
                data-testid={`feature-${idx}`}
                className={`group relative flex flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-7 transition-all duration-300 hover:-translate-y-1 hover:bg-white/[0.05] ${hoverBorder}`}
              >
                <span className={`grid place-items-center h-12 w-12 rounded-xl ring-1 ring-white/10 ${iconColor} ${iconBg}`}>
                  <Icon className="h-6 w-6" />
                </span>
                <h3 className="mt-5 font-outfit text-lg font-bold text-white">
                  {f.title}
                </h3>
                <ul className="mt-4 space-y-2.5">
                  {f.items.map((it) => (
                    <li key={it} className="flex gap-2.5 text-sm text-slate-400">
                      <Check className={`mt-0.5 h-4 w-4 shrink-0 ${dotMap[m.accent]}`} strokeWidth={3} />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};
